import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { Button } from './ui/Button';
import { Link2, Plus, LogOut, Shield, UserCheck, RefreshCw } from 'lucide-react';

interface NavbarProps {
  onOpenCreateModal?: () => void;
  activePath: string;
  onNavigate: (path: string) => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onOpenCreateModal, onNavigate }) => {
  const { user, devUserEmail, setDevUserEmail, logoutUrl } = useAuth();
  const isDevMode = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  return (
    <header className="sticky top-0 z-40 w-full border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-md">
      <div className="flex h-16 items-center justify-between px-4 sm:px-6 max-w-7xl mx-auto">
        {/* Brand & Title */}
        <div className="flex items-center gap-4">
          <div
            onClick={() => onNavigate('/admin')}
            className="flex items-center gap-2.5 cursor-pointer group"
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-orange-600 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-950/40 border border-orange-400/30 group-hover:scale-105 transition-transform">
              <Link2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-extrabold text-slate-100 tracking-tight text-base">
                  BROTOTYPE
                </span>
                <span className="text-xs px-2 py-0.5 rounded-md bg-orange-500/15 text-orange-400 font-semibold border border-orange-500/30">
                  LNK
                </span>
              </div>
              <p className="text-[10px] text-slate-400 tracking-wider uppercase font-medium">
                Marketing Link Manager
              </p>
            </div>
          </div>
        </div>

        {/* Right side actions */}
        <div className="flex items-center gap-3">
          {/* Environment Indicator / Dev Role Switcher */}
          {isDevMode ? (
            <div className="hidden md:flex items-center gap-2 px-2.5 py-1 rounded-lg bg-slate-900 border border-slate-800 text-xs">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <span className="text-slate-400">Dev Role:</span>
              <select
                value={devUserEmail}
                onChange={(e) => setDevUserEmail(e.target.value)}
                className="bg-transparent text-slate-200 font-medium focus:outline-none cursor-pointer"
                title="Switch simulated staff account in development"
              >
                <option value="marketing@brototype.com" className="bg-slate-900 text-slate-200">
                  marketing@brototype.com (Admin)
                </option>
                <option value="campaigns@brototype.com" className="bg-slate-900 text-slate-200">
                  campaigns@brototype.com (Staff)
                </option>
              </select>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-400">
              <Shield className="w-3.5 h-3.5" />
              <span>Cloudflare Access Active</span>
            </div>
          )}

          {/* Quick Create Link CTA */}
          <Button
            size="sm"
            onClick={onOpenCreateModal || (() => onNavigate('/admin/links/new'))}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Create Link
          </Button>

          {/* Staff Profile Chip */}
          {user && (
            <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-slate-800">
              <div className="w-8 h-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-bold text-slate-300">
                {user.email.charAt(0).toUpperCase()}
              </div>
              <div className="hidden lg:block text-left">
                <p className="text-xs font-semibold text-slate-200 truncate max-w-[140px]">{user.name || user.email}</p>
                <p className="text-[10px] text-slate-400 capitalize">{user.role.replace('_', ' ')}</p>
              </div>
            </div>
          )}

          {/* Logout button */}
          <a
            href={logoutUrl}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-900 transition-colors"
            title="Log out of Cloudflare Access"
            aria-label="Log out"
          >
            <LogOut className="w-4 h-4" />
          </a>
        </div>
      </div>
    </header>
  );
};
